"use client";

import { useIsDark } from "@/context/ThemeContext";
import Link from "next/link";
import { twMerge } from "tailwind-merge";
import { memo } from 'react';

const BASE_STYLES = "group relative inline-flex items-center justify-center gap-2 rounded-[7px] border border-[#FF1CF7] font-[600] font-[\"Albert_Sans\"] whitespace-nowrap transition-[background,box-shadow] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1CF7]/70 overflow-visible";
const SIZE_STYLES = "md:h-10 lg:h-12 3xl:h-14 4xl:h-16 px-3 lg:px-6 xl:px-7 3xl:px-8 4xl:px-9 py-1 lg:py-2 xl:py-3 3xl:py-4 4xl:py-5";
const TEXT_STYLES = "text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px] xl:text-[15px] 3xl:text-[18px] 4xl:text-[22px] leading-[18px]";
const THEME_STYLES = {
  light: "bg-[#DF27DF] text-white hover:bg-[#FF1CF7] transition-colors duration-300 ease-in-out",
  dark: "bg-[#DF27DF40] text-[#DF27DF]"
};
const HOVER_RING = "hover:ring-1 hover:ring-[#FF1CF7]/50 hover:ring-offset-1 hover:ring-offset-transparent";

const Button = memo(function Button({
  label = "",
  href,
  className = "",
  hoverOverlay = true,
  hoverOverlayClassName = "",
  glowOnHover = false,
  glowClassName = "",
  ringClassName = "",
  hoverRing = false,
  hoverRingClassName = "",
  children,
  ...props
}) {
  const isdark = useIsDark();

  const classes = twMerge(
    BASE_STYLES,
    SIZE_STYLES,
    TEXT_STYLES,
    isdark ? THEME_STYLES.dark : THEME_STYLES.light,
    props.disabled ? "cursor-not-allowed" : "cursor-pointer",
    hoverRing && !props.disabled ? HOVER_RING : "",
    hoverRing && !props.disabled ? hoverRingClassName : "",
    className
  );

  const labelCls = "relative z-[2]";

  const overlayCls = twMerge(
    "pointer-events-none absolute inset-0 z-[1] rounded-[7px] opacity-0",
    "transition-opacity duration-300 group-hover:opacity-100",
    "bg-[#FF1CF7]/10",
    hoverOverlayClassName
  );

  const glowCls = twMerge(
    "pointer-events-none absolute inset-0 z-[0] rounded-[7px] opacity-0",
    "transition-opacity duration-300 group-hover:opacity-100",
    "shadow-[0_12px_40px_rgba(255,28,247,0.5)]",
    glowClassName
  );

  const As = href ? Link : "button";

  return (
    <As
      {...(href ? { href } : { type: "button" })}
      className={classes}
      aria-label={label}
      {...props}
    >
      {hoverOverlay && <span aria-hidden className={overlayCls} />}
      {glowOnHover && <span aria-hidden className={glowCls} />}
      <span className={labelCls}>{children || label}</span>
    </As>
  );
});

export default Button;